import React, { useState } from 'react';
import { Globe, CheckCircle, XCircle, AlertCircle, RefreshCw, Loader2, Mail } from 'lucide-react';
import AdminNav from './AdminNav';

interface DNSRecord {
  configured: boolean;
  value: string | null;
  expected?: string;
  valid?: boolean;
  description?: string;
  name?: string;
  error?: string;
}

interface DNSResults {
  domain: string;
  timestamp: string;
  records: {
    mx: DNSRecord;
    spf: DNSRecord;
    dkim: DNSRecord;
    dmarc: DNSRecord;
  };
  summary: {
    total: number;
    configured: number;
    missing: number;
  };
  allConfigured: boolean;
  recommendations?: Array<{
    type: string;
    action: string;
    value: string;
  }>;
}

export default function DNSChecker() {
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState<DNSResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<any>(null);

  const checkDNS = async () => {
    setChecking(true);
    setError(null);
    setResults(null);

    try {
      // Check DNS records
      const dnsResponse = await fetch('https://birthday-coffee-api.b-9f2.workers.dev/api/admin/dns/check');
      if (!dnsResponse.ok) {
        throw new Error(`DNS check failed: ${dnsResponse.statusText}`);
      }
      const dnsData = await dnsResponse.json();
      setResults(dnsData);

      // Also check Resend status
      const resendResponse = await fetch('https://birthday-coffee-api.b-9f2.workers.dev/api/admin/dns/resend-status');
      if (resendResponse.ok) {
        const resendData = await resendResponse.json();
        setResendStatus(resendData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check DNS records');
    } finally {
      setChecking(false);
    }
  };

  const getStatusIcon = (configured: boolean, valid?: boolean) => {
    if (!configured) {
      return <XCircle className="w-5 h-5 text-red-400" />;
    }
    if (valid === false) {
      return <AlertCircle className="w-5 h-5 text-yellow-400" />;
    }
    return <CheckCircle className="w-5 h-5 text-green-400" />;
  };

  const getStatusColor = (configured: boolean, valid?: boolean) => {
    if (!configured) return 'text-red-400';
    if (valid === false) return 'text-yellow-400';
    return 'text-green-400';
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <AdminNav
        title="DNS Checker"
        description="Check DNS configuration and domain settings"
      />
      <div className="py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="bg-slate-800 rounded-xl shadow-2xl p-8">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <Globe className="w-10 h-10 text-blue-400" />
                <div>
                  <h2 className="text-2xl font-bold text-white">Email Authentication Records</h2>
                  <p className="text-slate-400">Verify DNS configuration for updates.birthday.coffee</p>
                </div>
              </div>
            <button
              onClick={checkDNS}
              disabled={checking}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center gap-2"
            >
              {checking ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  Check DNS Records
                </>
              )}
            </button>
          </div>

          {/* Resend Status */}
          {resendStatus && (
            <div className="bg-blue-900 border border-blue-700 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3">
                <Mail className="w-6 h-6 text-blue-400" />
                <div>
                  <h3 className="text-blue-200 font-semibold">Resend Domain Status</h3>
                  <p className="text-blue-300 text-sm mt-1">{resendStatus.message}</p>
                  {resendStatus.verified && (
                    <p className="text-green-400 text-sm mt-1 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Domain is verified in Resend
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-900 border border-red-700 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-red-400" />
                <div>
                  <h3 className="text-red-200 font-semibold">Error</h3>
                  <p className="text-red-300">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          {results && (
            <>
              {/* Summary */}
              <div className="bg-slate-700 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-white">DNS Records Summary</h2>
                  <span className={`text-sm ${results.allConfigured ? 'text-green-400' : 'text-yellow-400'}`}>
                    {results.summary.configured}/{results.summary.total} Configured
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-slate-800 rounded p-4">
                    <p className="text-slate-400 text-sm mb-1">Total Records</p>
                    <p className="text-2xl font-bold text-white">{results.summary.total}</p>
                  </div>
                  <div className="bg-slate-800 rounded p-4">
                    <p className="text-slate-400 text-sm mb-1">Configured</p>
                    <p className="text-2xl font-bold text-green-400">{results.summary.configured}</p>
                  </div>
                  <div className="bg-slate-800 rounded p-4">
                    <p className="text-slate-400 text-sm mb-1">Missing</p>
                    <p className="text-2xl font-bold text-red-400">{results.summary.missing}</p>
                  </div>
                </div>

                <p className="text-slate-400 text-xs mt-4">
                  Last checked: {new Date(results.timestamp).toLocaleString()}
                </p>
              </div>

              {/* MX Record */}
              <div className="bg-slate-700 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  MX Record (Mail Exchange)
                  {getStatusIcon(results.records.mx.configured, results.records.mx.valid)}
                </h3>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="text-slate-400 text-sm w-20">Domain:</span>
                    <code className="text-blue-400 text-xs">send.updates.birthday.coffee</code>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-slate-400 text-sm w-20">Status:</span>
                    <span className={`text-sm ${getStatusColor(results.records.mx.configured, results.records.mx.valid)}`}>
                      {results.records.mx.configured ? 'Configured' : 'Not Configured'}
                    </span>
                  </div>
                  {results.records.mx.value && (
                    <div className="flex items-start gap-2">
                      <span className="text-slate-400 text-sm w-20">Current:</span>
                      <code className="text-green-400 text-xs bg-slate-800 px-2 py-1 rounded break-all">
                        {results.records.mx.value}
                      </code>
                    </div>
                  )}
                </div>
              </div>

              {/* SPF Record */}
              <div className="bg-slate-700 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  SPF Record
                  {getStatusIcon(results.records.spf.configured, results.records.spf.valid)}
                </h3>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="text-slate-400 text-sm w-20">Domain:</span>
                    <code className="text-blue-400 text-xs">send.updates.birthday.coffee</code>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-slate-400 text-sm w-20">Status:</span>
                    <span className={`text-sm ${getStatusColor(results.records.spf.configured, results.records.spf.valid)}`}>
                      {results.records.spf.configured ? 'Configured' : 'Not Configured'}
                      {results.records.spf.configured && results.records.spf.valid === false && ' (Invalid)'}
                    </span>
                  </div>
                  {results.records.spf.value && (
                    <div className="flex items-start gap-2">
                      <span className="text-slate-400 text-sm w-20">Current:</span>
                      <code className="text-green-400 text-xs bg-slate-800 px-2 py-1 rounded break-all">
                        {results.records.spf.value}
                      </code>
                    </div>
                  )}
                  <div className="flex items-start gap-2">
                    <span className="text-slate-400 text-sm w-20">Expected:</span>
                    <code className="text-blue-400 text-xs bg-slate-800 px-2 py-1 rounded">
                      {results.records.spf.expected}
                    </code>
                  </div>
                </div>
              </div>

              {/* DKIM Record */}
              <div className="bg-slate-700 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  DKIM Record
                  {getStatusIcon(results.records.dkim.configured, results.records.dkim.valid)}
                </h3>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="text-slate-400 text-sm w-20">Domain:</span>
                    <code className="text-blue-400 text-xs">resend._domainkey.updates.birthday.coffee</code>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-slate-400 text-sm w-20">Status:</span>
                    <span className={`text-sm ${getStatusColor(results.records.dkim.configured, results.records.dkim.valid)}`}>
                      {results.records.dkim.configured ? 'Configured' : 'Not Configured'}
                    </span>
                  </div>
                  {results.records.dkim.value && (
                    <div className="flex items-start gap-2">
                      <span className="text-slate-400 text-sm w-20">Public Key:</span>
                      <code className="text-green-400 text-xs bg-slate-800 px-2 py-1 rounded">
                        {results.records.dkim.value}
                      </code>
                    </div>
                  )}
                </div>
              </div>

              {/* DMARC Record */}
              <div className="bg-slate-700 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  DMARC Record (Optional)
                  {getStatusIcon(results.records.dmarc.configured, results.records.dmarc.valid)}
                </h3>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="text-slate-400 text-sm w-20">Domain:</span>
                    <code className="text-blue-400 text-xs">_dmarc.birthday.coffee</code>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-slate-400 text-sm w-20">Status:</span>
                    <span className={`text-sm ${getStatusColor(results.records.dmarc.configured, results.records.dmarc.valid)}`}>
                      {results.records.dmarc.configured ? 'Configured' : 'Not Configured (Optional)'}
                    </span>
                  </div>
                  {results.records.dmarc.value && (
                    <div className="flex items-start gap-2">
                      <span className="text-slate-400 text-sm w-20">Policy:</span>
                      <code className="text-green-400 text-xs bg-slate-800 px-2 py-1 rounded">
                        {results.records.dmarc.value}
                      </code>
                    </div>
                  )}
                </div>
              </div>

              {/* Recommendations */}
              {results.recommendations && results.recommendations.length > 0 && (
                <div className="bg-yellow-900 border border-yellow-700 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-yellow-200 mb-4 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Recommended Actions
                  </h3>
                  <div className="space-y-3">
                    {results.recommendations.map((rec, index) => (
                      <div key={index} className="bg-yellow-800/50 rounded p-3">
                        <p className="text-yellow-300 text-sm font-semibold">{rec.type}</p>
                        <p className="text-yellow-200 text-sm mt-1">{rec.action}</p>
                        <code className="text-xs text-yellow-100 block mt-2 bg-slate-900 px-2 py-1 rounded">
                          {rec.value}
                        </code>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Instructions */}
          {!results && !checking && (
            <div className="bg-slate-700 rounded-lg p-6">
              <h3 className="text-white font-semibold mb-3">📝 What this tool checks:</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                  <span><strong>MX Record:</strong> Routes bounce and feedback emails to Amazon SES</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                  <span><strong>SPF Record:</strong> Authorizes Amazon SES to send emails on behalf of send.updates.birthday.coffee</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                  <span><strong>DKIM Record:</strong> Cryptographic signature that verifies email authenticity</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                  <span><strong>DMARC (Optional):</strong> Policy for how to handle unauthenticated emails</span>
                </li>
              </ul>
              <p className="text-slate-400 text-xs mt-4">
                These records ensure your emails are properly authenticated and less likely to be marked as spam.
              </p>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}